import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DrawerModule } from 'primeng/drawer';
import { SelectModule } from 'primeng/select';
import { DatePickerModule } from 'primeng/datepicker';
import { CheckboxModule } from 'primeng/checkbox';
import { SkeletonModule } from 'primeng/skeleton';

import { TooltipModule } from 'primeng/tooltip';
import { Menu, MenuModule } from 'primeng/menu';
import { PaginatorModule } from 'primeng/paginator';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timeout } from 'rxjs/operators';
import {
  EscalaService,
  PostoService,
  EfetivoService,
  ClienteService,
} from '@core/services/domain-services';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { FilterPanelComponent } from '@shared/components/filter-panel/filter-panel.component';
import {
  Escala,
  EscalaEfetivo,
  Posto,
  Efetivo,
  Cliente,
  EnumGenero,
} from '@core/models/models';
import { obterEstadoEscalaVisual } from './escala-estado.util';

@Component({
  selector: 'app-escalas',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    DrawerModule,
    SelectModule,
    DatePickerModule,
    CheckboxModule,
    SkeletonModule,

    TooltipModule,
    MenuModule,
    PaginatorModule,
    EmptyStateComponent,
    BreadcrumbComponent,
    FilterPanelComponent,
  ],

  templateUrl: './escalas.component.html',
  styleUrl: './escalas.component.scss',
})
export class EscalasComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly svc = inject(EscalaService);
  private readonly postoSvc = inject(PostoService);
  private readonly efetivoSvc = inject(EfetivoService);
  private readonly clienteSvc = inject(ClienteService);
  private readonly msg = inject(MessageService);
  private readonly confirm = inject(ConfirmationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  escalas: Escala[] = [];
  postos: Posto[] = [];
  efetivos: Efetivo[] = [];
  clientes: Cliente[] = [];

  @ViewChild('kebabMenu') kebabMenu!: Menu;
  escalaActiva: Escala | null = null;

  loading = false;
  showDialog = false;
  showFilters = false;
  editing: Escala | null = null;
  submitting = false;
  form!: FormGroup;
  page = 1;
  pageSize = 10;
  totalRecords = 0;

  filtroPosto: number | null = null;
  filtroMes: Date = new Date();
  filtroCliente: number | null = null;
  filtroEfetivo: number | null = null;
  filtroEstado: number | null = null;
  filtroCobertura: string | null = null;
  readonly coberturaOpts = [
    { label: 'Diurno', value: 'DIURNO', icon: 'ri-sun-line' },
    { label: 'Noturno', value: 'NOTURNO', icon: 'ri-moon-line' },
    { label: '24 Horas', value: '24_HORAS', icon: 'ri-time-line' },
  ];

  readonly coberturaFiltroOpts = [
    { label: 'Diurno', value: 'DIURNO' },
    { label: 'Noturno', value: 'NOTURNO' },
    { label: '24 Horas', value: '24_HORAS' },
  ];

  readonly estadoOpts = [
    { label: 'Agendada', value: 0 },
    { label: 'Em Execução', value: 1 },
    { label: 'Concluída', value: 2 },
    { label: 'Cancelada', value: 3 },
  ];

  readonly mockEfetivos: Efetivo[] = [
    {
      id: 1,
      nome: 'João Silva',
      numBI: '001LA036',
      dataNascimento: '1992-03-15',
      genero: EnumGenero.MASCULINO,
      numTelefone: '923 456 789',
      idFuncao: 1,
      idCategoria: 1,
      dataAdmissao: '2020-06-01',
      ativo: true,
    },
    {
      id: 2,
      nome: 'Maria Santos',
      numBI: '002LA036',
      dataNascimento: '1988-05-15',
      genero: EnumGenero.FEMININO,
      numTelefone: '923 555 888',
      idFuncao: 1,
      idCategoria: 1,
      dataAdmissao: '2019-06-01',
      ativo: true,
    },
    {
      id: 3,
      nome: 'Paulo Ferreira',
      numBI: '003LA036',
      dataNascimento: '1990-09-20',
      genero: EnumGenero.MASCULINO,
      numTelefone: '923 777 999',
      idFuncao: 2,
      idCategoria: 1,
      dataAdmissao: '2021-03-15',
      ativo: true,
    },
    {
      id: 4,
      nome: 'Ana Rodrigues',
      numBI: '004LA036',
      dataNascimento: '1995-11-10',
      genero: EnumGenero.FEMININO,
      numTelefone: '923 111 222',
      idFuncao: 1,
      idCategoria: 2,
      dataAdmissao: '2022-07-01',
      ativo: true,
    },
    {
      id: 5,
      nome: 'Carlos Gomes',
      numBI: '005LA036',
      dataNascimento: '1987-03-25',
      genero: EnumGenero.MASCULINO,
      numTelefone: '923 333 444',
      idFuncao: 3,
      idCategoria: 2,
      dataAdmissao: '2018-11-20',
      ativo: true,
    },
  ];

  readonly mockPostos: Posto[] = [
    {
      id: 1,
      nome: 'Posto Central – Luanda',
      codigo: 'PST-001',
      ativo: true,
      risco: 'Baixo',
      numEfetivosTurno: 2,
      numTurnosDia: 2,
    },
    {
      id: 2,
      nome: 'Posto Norte – Cacuaco',
      codigo: 'PST-002',
      ativo: true,
      risco: 'Moderado',
      numEfetivosTurno: 3,
      numTurnosDia: 3,
    },
    {
      id: 3,
      nome: 'Posto Sul – Viana',
      codigo: 'PST-003',
      ativo: true,
      risco: 'Alto',
      numEfetivosTurno: 4,
      numTurnosDia: 2,
    },
    {
      id: 4,
      nome: 'Posto Leste – Talatona',
      codigo: 'PST-004',
      ativo: true,
      risco: 'Baixo',
      numEfetivosTurno: 2,
      numTurnosDia: 3,
    },
    {
      id: 5,
      nome: 'Posto Aeroporto – Luanda',
      codigo: 'PST-005',
      ativo: true,
      risco: 'Critico',
      numEfetivosTurno: 5,
      numTurnosDia: 3,
    },
  ];

  ngOnInit(): void {
    this.initForm();
    this.loadPostos();
    this.loadEfetivos();
    this.loadClientes();

    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (params['novaEscala'] === 'true') {
          const idPosto = params['idPosto'] || params['PostoId'];
          this.router.navigate(
            ['/admin/escalas/nova'],
            idPosto ? { queryParams: { idPosto } } : {},
          );
        }
      });

    this.load();
  }

  initForm(): void {
    this.form = this.fb.group({
      idEfetivo: [null, Validators.required],
      idPosto: [null, Validators.required],
      dataInicio: [null, Validators.required],
      dataTermino: [null, Validators.required],
      cobertura: ['DIURNO', Validators.required],
      escalaSubstituicao: [false],
    });
  }

  loadPostos(): void {
    this.postoSvc
      .getAll()
      .pipe(timeout(6000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.postos = res.data?.length ? res.data : this.mockPostos;
        },
        error: () => {
          this.postos = this.mockPostos;
        },
      });
  }

  loadEfetivos(): void {
    this.efetivoSvc
      .getAll()
      .pipe(timeout(6000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.efetivos = res.data?.length ? res.data : this.mockEfetivos;
        },
        error: () => {
          this.efetivos = this.mockEfetivos;
        },
      });
  }

  loadClientes(): void {
    this.clienteSvc
      .getAll({ pageSize: 200 })
      .pipe(timeout(6000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.clientes = res.data ?? [];
        },
        error: () => {
          this.clientes = [];
        },
      });
  }

  load(): void {
    this.loading = true;
    const params: Record<string, unknown> = {
      pageNumber: this.page,
      pageSize: this.pageSize,
    };
    if (this.filtroCobertura) params['cobertura'] = this.filtroCobertura;
    if (this.filtroPosto) params['PostoId'] = this.filtroPosto;
    if (this.filtroCliente) params['ClienteId'] = this.filtroCliente;
    if (this.filtroEfetivo) params['EfetivoId'] = this.filtroEfetivo;
    if (this.filtroEstado !== null && this.filtroEstado !== undefined)
      params['Estado'] = this.filtroEstado;
    if (this.filtroMes) {
      const ano = this.filtroMes.getFullYear();
      const mes = this.filtroMes.getMonth();
      const inicio = new Date(ano, mes, 1);
      const fim = new Date(ano, mes + 1, 0, 23, 59, 59, 999);
      const toApi = (d: Date) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const da = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${da}T00:00:00`;
      };
      params['DataInicioDe'] = toApi(inicio);
      params['DataInicioAte'] = `${fim.getFullYear()}-${String(fim.getMonth() + 1).padStart(2, '0')}-${String(fim.getDate()).padStart(2, '0')}T23:59:59`;
    }

    this.svc
      .getAll(params)
      .pipe(timeout(8000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const totalDaApi = res.totalRecords ?? res.data?.length ?? 0;
          const dadosApi: Escala[] = res.data ?? [];
          const dados = this.filtrarPorCobertura(dadosApi);
          this.escalas = dados;
          this.totalRecords = this.filtroCobertura ? dados.length : totalDaApi;
          this.loading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.escalas = this.buildMockEscalas();
          this.totalRecords = this.escalas.length;
          this.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  private filtrarPorCobertura(dados: Escala[]): Escala[] {
    if (!this.filtroCobertura) return dados;

    const cobertura = this.filtroCobertura;

    return dados.filter((e) => {
      const turno = (e.nomeTurno ?? '').toLowerCase();

      if (cobertura === 'NOTURNO') {
        return turno.includes('noturno');
      }

      if (cobertura === '24_HORAS') {
        return turno.includes('24 horas');
      }

      if (cobertura === 'DIURNO') {
        return !turno.includes('noturno') && !turno.includes('24 horas');
      }

      return true;
    });
  }

  applyFilters(): void {
    this.page = 1;
    this.showFilters = false;
    this.load();
  }

  onPageChange(ev: any): void {
    this.page = (ev.page ?? 0) + 1;
    this.pageSize = ev.rows ?? this.pageSize;
    this.load();
  }

  private buildMockEscalas(): Escala[] {
    const hoje = new Date();
    const ini = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01 00:00:00`;
    const fim = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-30 23:59:00`;
    const mock = (
      id: number,
      idPosto: number,
      estado: string,
      efs: EscalaEfetivo[],
      turno: string,
    ) =>
      ({
        id,
        idPosto,
        nomePosto: this.mockPostos[idPosto - 1]?.nome ?? `Posto #${idPosto}`,
        turnoId: 1,
        nomeTurno: turno,
        estado,
        efetivos: efs,
        dataInicio: ini,
        dataFim: fim,
      }) as Escala;
    const ef = (id: number, nome: string): EscalaEfetivo => ({
      id,
      nome,
      equipamentos: [],
    });
    return [
      mock(
        1,
        1,
        'Decorrendo',
        [ef(1, 'João Silva'), ef(2, 'Maria Santos')],
        '24/24',
      ),
      mock(2, 2, 'Agendada', [ef(3, 'Paulo Ferreira')], 'Diurno'),
      mock(3, 1, 'Concluida', [ef(4, 'Ana Rodrigues')], 'Noturno'),
      mock(4, 3, 'Agendada', [], '24/24'),
    ];
  }

  get totalRecordsCount(): number {
    return this.totalRecords;
  }
  get escalaAtivasCount(): number {
    return this.escalas.filter(
      (e) => e.estado === 'Decorrendo' || (e as any).estadoDescricao?.toLowerCase() === 'a decorrer' || (e as any).estado === 1
    ).length;
  }

  get escalaAgendadasCount(): number {
    return this.escalas.filter(
      (e) => e.estado === 'Agendada' || (e as any).estadoDescricao?.toLowerCase() === 'agendada' || (e as any).estado === 0
    ).length;
  }

  get mesLabel(): string {
    return this.filtroMes.toLocaleDateString('pt-AO', {
      month: 'long',
      year: 'numeric',
    });
  }

  mesAnterior(): void {
    this.filtroMes = new Date(
      this.filtroMes.getFullYear(),
      this.filtroMes.getMonth() - 1,
      1,
    );
    this.page = 1;
    this.load();
  }

  mesSeguinte(): void {
    this.filtroMes = new Date(
      this.filtroMes.getFullYear(),
      this.filtroMes.getMonth() + 1,
      1,
    );
    this.page = 1;
    this.load();
  }

  openNew(): void {
    this.router.navigate(['/admin/escalas/nova']);
  }

  openEdit(e: Escala): void {
    this.editing = e;
    this.form.patchValue({
      ...e,
      cobertura: e.cobertura || 'DIURNO',
      dataInicio: e.dataInicio ? new Date(e.dataInicio) : null,
      dataTermino: e.dataTermino ? new Date(e.dataTermino) : null,
    });
    this.showDialog = true;
  }

  hasError(field: string): boolean {
    const c = this.form.get(field);
    return !!(c?.invalid && c?.touched);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.submitting = true;
    const v = this.form.value;
    const dto = {
      ...v,
      dataInicio:
        v.dataInicio instanceof Date
          ? v.dataInicio.toISOString()
          : v.dataInicio,
      dataTermino:
        v.dataTermino instanceof Date
          ? v.dataTermino.toISOString()
          : v.dataTermino,
    };
    const obs = this.editing?.id
      ? this.svc.update(this.editing.id, dto)
      : this.svc.create(dto);

    obs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.msg.add({
          severity: 'success',
          summary: 'Sucesso',
          detail: 'Escala guardada',
          life: 3000,
        });
        this.showDialog = false;
        this.submitting = false;
        this.load();
      },
      error: () => {
        this.submitting = false;
      },
    });
  }

  confirmDelete(e: Escala): void {
    this.confirm.confirm({
      header: 'Eliminar Escala',
      message: 'Esta escala será eliminada. Deseja continuar?',
      icon: 'ri-alert-line',
      accept: () => this.delete(e),
    });
  }

  private delete(e: Escala): void {
    if (!e.id) return;
    this.svc
      .delete(e.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Eliminado',
            detail: 'Escala eliminada',
            life: 3000,
          });
          this.load();
        },
      });
  }

  formatDate(d: string | Date | undefined | null): string {
    if (!d) return '—';
    const iso = typeof d === 'string' ? d.replace(' ', 'T') : d;
    const dt = new Date(iso);
    if (isNaN(dt.getTime())) return String(d);
    return dt.toLocaleDateString('pt-AO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  }

  getNomeEfetivo(e: Escala): string {
    if (e.efetivos && e.efetivos.length > 0) {
      if (e.efetivos.length === 1) return e.efetivos[0].nome;
      return `${e.efetivos[0].nome} +${e.efetivos.length - 1}`;
    }
    return (e as any).nomeEfetivo ?? e.efetivo?.nome ?? '—';
  }

  getInitialsEscala(e: Escala): string {
    const nome = this.getNomeEfetivo(e);
    if (nome === '—') return '?';
    return this.getInitials(nome);
  }

  getNomePosto(e: Escala): string {
    return e.nomePosto ?? e.posto?.nome ?? `Posto #${e.idPosto}`;
  }

  getEquipamentosEscala(e: Escala): string {
    if (!e.efetivos?.length) return '';
    const equips = e.efetivos.flatMap((ef) => ef.equipamentos ?? []);
    if (!equips.length) return '';
    if (equips.length === 1) return equips[0].codigo;
    return `${equips[0].codigo} +${equips.length - 1}`;
  }

  getAvaliacaoEscala(e: Escala): number {
    if (!e.efetivos?.length) return 0;
    const vals = e.efetivos
      .map((ef) => ef.avaliacaoFim ?? 0)
      .filter((v) => v > 0);
    if (!vals.length) return 0;
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }

  get menuItems(): MenuItem[] {
    const e = this.escalaActiva;
    return [
      {
        label: 'Ver Detalhe',
        icon: 'ri ri-eye-line',
        command: () => e && this.verDetalheEscala(e),
      },
      {
        label: 'Editar',
        icon: 'ri ri-pencil-line',
        visible: e ? ['agendada', 'decorrendo'].includes(obterEstadoEscalaVisual(e)) : false,
        command: () => e && this.openEdit(e),
      },
      {
        label: 'Ver Efetivo',
        icon: 'ri ri-user-line',
        visible: !!e?.efetivos?.length,
        command: () => e && this.verDetalheEfetivo(e),
      },
      {
        label: 'Ver Posto',
        icon: 'ri ri-map-pin-line',
        command: () => e && this.verDetalhePosto(e),
      },
      { separator: true },
      {
        label: 'Eliminar',
        icon: 'ri ri-delete-bin-line',
        visible: e ? obterEstadoEscalaVisual(e) === 'agendada' : false,
        styleClass: 'danger-item',
        command: () => e && this.confirmDelete(e),
      },
    ];
  }

  abrirMenu(event: Event, e: Escala): void {
    this.escalaActiva = e;
    this.cdr.detectChanges();
    this.kebabMenu.toggle(event);
  }

  abrirDetalheEscala(event: MouseEvent | KeyboardEvent, e: Escala): void {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const control = target.closest(
      'button, a, input, select, textarea, [role="button"], [role="menuitem"]',
    );
    if (control && control !== event.currentTarget) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }

    this.verDetalheEscala(e);
  }

  verDetalheEscala(e: Escala): void {
    this.router.navigate(['/admin/escalas', e.id]);
  }

  verDetalheEfetivo(e: Escala): void {
    const id = e.efetivos?.[0]?.id ?? e.idEfetivo;
    if (id) this.router.navigate(['/admin/efetivos', id]);
  }

  verDetalhePosto(e: Escala): void {
    this.router.navigate(['/admin/postos', e.idPosto]);
  }

  getEstadoClass(estado?: Escala['estado'], desc?: string): string {
    const rawDesc = (desc ?? '').toString().toLowerCase();
    if (rawDesc.includes('decorr') || rawDesc.includes('exec')) return 'estado-decorrendo';
    if (rawDesc.includes('agend')) return 'estado-agendada';
    if (rawDesc.includes('conclu')) return 'estado-concluida';
    if (rawDesc.includes('cancel')) return 'estado-default';
    if (rawDesc.includes('ativa')) return 'estado-ativa';
    return 'estado-default';
  }

  getEstadoLabel(e: Escala): string {
    if (e.estadoDescricao) return e.estadoDescricao;
    return '—';
  }

  getInitials(nome: string): string {
    return nome
      .split(' ')
      .filter((n) => n.length > 0)
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }

  limparFiltros(): void {
    this.filtroPosto = null;
    this.filtroCliente = null;
    this.filtroEfetivo = null;
    this.filtroEstado = null;
    this.filtroCobertura = null;
    this.page = 1;
    this.load();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filtroCliente ||
      this.filtroEfetivo ||
      this.filtroEstado !== null ||
      this.filtroCobertura
    );
  }

  isAtiva(e: Escala): boolean {
    const hoje = new Date();
    const fim = e.dataFim ?? e.dataTermino;
    if (!e.dataInicio || !fim) return false;
    return (
      new Date(e.dataInicio.replace(' ', 'T')) <= hoje &&
      hoje <= new Date(fim.replace(' ', 'T'))
    );
  }

  getDuracao(e: Escala): string {
    const fim = e.dataFim ?? e.dataTermino;
    if (!e.dataInicio || !fim) return '—';
    const ini = new Date(e.dataInicio.replace(' ', 'T'));
    const f = new Date(fim.replace(' ', 'T'));
    const dias = Math.round((f.getTime() - ini.getTime()) / 86_400_000);
    if (dias === 0) return 'Mesmo dia';
    if (dias < 30) return `${dias} dia${dias !== 1 ? 's' : ''}`;
    const meses = Math.floor(dias / 30);
    return `${meses} mês${meses !== 1 ? 'es' : ''}`;
  }

  coberturaLabel(c?: string): string {
    const map: Record<string, string> = {
      DIURNO: 'Diurno',
      NOTURNO: 'Noturno',
      '24_HORAS': '24h',
    };
    return map[c ?? ''] || c || '—';
  }

  setCoberturaForm(val: string): void {
    this.form.get('cobertura')?.setValue(val);
  }
}
